// ============================================================
// shrine.null.js — Reconstructed for Shrine theme (Shopify)
// v2 — Autoplay fix included
// ============================================================

// ── Utility: debounce ────────────────────────────────────────
function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

// ── Utility: trapFocus / removeTrapFocus ─────────────────────
function trapFocus(container, elementToFocus = container) {
  const focusableElements = Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
    )
  );
  if (!focusableElements.length) {
    try { elementToFocus.focus(); } catch (e) {}
    return;
  }
  const first = focusableElements[0];
  const last  = focusableElements[focusableElements.length - 1];

  const handler = (e) => {
    if (e.code?.toUpperCase() !== 'TAB') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  };

  if (container._trapFocusHandler) {
    document.removeEventListener('keydown', container._trapFocusHandler);
  }
  container._trapFocusHandler = handler;
  document.addEventListener('keydown', handler);

  try { elementToFocus.focus(); } catch (e) {}
}

function removeTrapFocus(elementToFocus) {
  document.querySelectorAll('*').forEach(el => {
    if (el._trapFocusHandler) {
      document.removeEventListener('keydown', el._trapFocusHandler);
      el._trapFocusHandler = null;
    }
  });
  if (elementToFocus) {
    try { elementToFocus.focus(); } catch (e) {}
  }
}

// ── window.pauseAllMedia ──────────────────────────────────────
window.pauseAllMedia = function () {
  document.querySelectorAll('.js-youtube').forEach(video => {
    video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach(video => {
    video.contentWindow?.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach(video => {
    video.pause();
  });
  document.querySelectorAll('product-model').forEach(model => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
  document.querySelectorAll('internal-video').forEach(iv => {
    const video = iv.querySelector('video');
    if (video) video.pause();
    iv.classList.remove('internal-video--playing');
  });
};

// ── InternalVideo Custom Element ──────────────────────────────
class InternalVideo extends HTMLElement {

  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    this.playBtn    = this.querySelector('.internal-video__play');
    this.timeline   = this.querySelector('.internal-video__timeline');
    this.soundBtn   = this.querySelector('.internal-video__sound-btn');
    this.isAutoplay = this.dataset.autoplay === 'true';

    // Sources stay lazy (data-src) until the element enters the viewport.
    // Activation happens inside _tryPlay()/_setupManualPlay() — see _activateSources().

    if (this.isAutoplay) {
      this._setupAutoplay();
    } else {
      this._setupManualPlay();
    }

    if (this.soundBtn) {
      this.soundBtn.addEventListener('click', () => this._toggleSound());
    }
  }

  // Move data-src → src so the browser can actually load the video
  _activateSources() {
    this.querySelectorAll('source[data-src]').forEach(source => {
      source.setAttribute('src', source.getAttribute('data-src'));
      source.removeAttribute('data-src');
    });
  }

  _setupAutoplay() {
    const video = this.video;

    // Force-set required attributes for autoplay to work
    video.muted       = true;
    video.loop        = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('loop', '');

    this.classList.add('internal-video--muted');

    // No eager play/load: the IntersectionObserver below fires an initial callback right
    // after observe(). If visible -> load+play then; if off-screen -> stays readyState 0
    // (no download) until scrolled into view.

    // Use IntersectionObserver to play/pause as the element enters/leaves viewport
    if ('IntersectionObserver' in window) {
      this._intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._tryPlay();
          } else {
            video.pause();
            this.classList.remove('internal-video--playing');
          }
        });
      }, { threshold: 0 }); // 0 = fire as soon as a single pixel is visible

      this._intersectionObserver.observe(this);
    }

    // In autoplay mode the play button acts as a sound toggle
    if (this.playBtn) {
      this.playBtn.addEventListener('click', () => this._toggleSound());
    }

    if (this.timeline) {
      video.addEventListener('timeupdate', () => this._updateTimeline());
    }
  }

  _tryPlay() {
    const video = this.video;
    if (!video) return;

    // Lazily activate sources (data-src -> src) the first time we actually need to play.
    this._activateSources();

    // If no data has been loaded yet, call load() first
    if (video.readyState === 0) {
      video.load();
    }

    const promise = video.play();
    if (promise !== undefined) {
      promise
        .then(() => {
          this.classList.add('internal-video--playing');
        })
        .catch(() => {
          // Autoplay policy blocked it — retry after first user interaction
          const retry = () => {
            video.play()
              .then(() => this.classList.add('internal-video--playing'))
              .catch(() => {});
          };
          document.addEventListener('click',      retry, { once: true });
          document.addEventListener('touchstart', retry, { once: true });
          document.addEventListener('scroll',     retry, { once: true });
        });
    }
  }

  _setupManualPlay() {
    // Manual videos already carry a real src; activate defensively in case of data-src.
    this._activateSources();
    if (this.playBtn) {
      this.playBtn.addEventListener('click', () => this._togglePlay());
    }
    this.video.addEventListener('ended', () => {
      this.classList.remove('internal-video--playing');
    });
    if (this.timeline) {
      this.video.addEventListener('timeupdate', () => this._updateTimeline());
    }
  }

  _togglePlay() {
    if (this.video.paused) {
      window.pauseAllMedia();
      if (this.video.readyState === 0) this.video.load();
      this.video.play()
        .then(() => this.classList.add('internal-video--playing'))
        .catch(() => {});
    } else {
      this.video.pause();
      this.classList.remove('internal-video--playing');
    }
  }

  _toggleSound() {
    this.video.muted = !this.video.muted;
    this.classList.toggle('internal-video--muted', this.video.muted);
  }

  _updateTimeline() {
    if (!this.timeline || !this.video.duration) return;
    const pct = (this.video.currentTime / this.video.duration) * 100;
    this.timeline.style.width = pct + '%';
  }

  disconnectedCallback() {
    if (this._intersectionObserver) {
      this._intersectionObserver.disconnect();
    }
  }
}

customElements.define('internal-video', InternalVideo);

// ── StickyHeader ─────────────────────────────────────────────
class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector('.section-header');
    this.currentScrollTop = 0;
    this.onScrollHandler = debounce(this.onScroll.bind(this), 10);
    window.addEventListener('scroll', this.onScrollHandler, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollHandler);
  }

  onScroll() {
    if (!this.header) return;
    const scrollTop = window.scrollY;
    if (scrollTop > this.currentScrollTop && scrollTop > 50) {
      this.header.classList.add('shopify-section-header-hidden');
    } else {
      this.header.classList.remove('shopify-section-header-hidden');
    }
    this.currentScrollTop = scrollTop;
  }
}

if (!customElements.get('sticky-header')) {
  customElements.define('sticky-header', StickyHeader);
}

// ── CartDrawer (basic) ───────────────────────────────────────
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.querySelector('[id^="CartDrawer-Overlay"]')
      ?.addEventListener('click', this.close.bind(this));
    document.addEventListener('cart:open', () => this.open());
  }

  open() {
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    trapFocus(this, this.querySelector('[role="dialog"]'));
  }

  close() {
    this.removeAttribute('open');
    document.body.classList.remove('overflow-hidden');
    removeTrapFocus();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// ── Animations init ──────────────────────────────────────────
(function initAnimations() {
  const scriptEl = document.querySelector('script[data-animations-type]');
  const animType = scriptEl?.dataset?.animationsType;
  if (!animType || animType === 'none') return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animated');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1 });

  document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
})();

// ── Country/region selector (stub) ───────────────────────────
(function initCountrySelector() {
  const scriptEl = document.querySelector('script[data-country-list-function]');
  if (!scriptEl) return;
  const fn = scriptEl.dataset.countryListFunction;
  if (fn && typeof window[fn] === 'function') {
    try { window[fn](); } catch (e) {}
  }
})();